import { makeFormatDateTick, TICK_FONT_SIZE } from './cartesianChart'
import type { ChartDataRow } from './cartesianChart'
import { DAILY_TICK_LIMIT, DEFAULT_X_TICK_COUNT } from './xAxisTicks'

import type { JSX } from 'react'

/** Extra x-axis height for the stacked comparison-period date labels. */
export const X_AXIS_HEIGHT_STACKED = 44

const PRIMARY_TICK_DY = 12
const COMPARISON_TICK_DY = 28
const COMPARISON_TICK_FONT_SIZE = 11
const COMPARISON_TICK_OPACITY = 0.7

interface StackedDateTickProps {
  x?: string | number
  y?: string | number
  payload?: { value?: number }
  color?: string
  comparisonDateByTimestamp: Map<number, string>
  comparisonOnly?: boolean
  formatTick?: (timestamp: number) => string
}

/**
 * X-axis tick that stacks the comparison-period date beneath the primary date.
 * Shared by the time-axis charts (TimeSeriesChart, ComposedChart) that overlay a
 * comparison period.
 */
export function StackedDateTick({
  x = 0,
  y = 0,
  payload,
  color,
  comparisonDateByTimestamp,
  comparisonOnly = false,
  formatTick = makeFormatDateTick(),
}: StackedDateTickProps): JSX.Element {
  const ts = payload?.value
  const primary = ts != null ? formatTick(Number(ts)) : ''
  const comparisonIso =
    ts != null ? comparisonDateByTimestamp.get(Number(ts)) : undefined
  const comparison = comparisonIso
    ? formatTick(new Date(comparisonIso).getTime())
    : null

  if (comparisonOnly) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={PRIMARY_TICK_DY}
          textAnchor="middle"
          fontSize={TICK_FONT_SIZE}
          fill={color}
        >
          {comparison ?? primary}
        </text>
      </g>
    )
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={PRIMARY_TICK_DY}
        textAnchor="middle"
        fontSize={TICK_FONT_SIZE}
        fill={color}
      >
        {primary}
      </text>
      {comparison && (
        <text
          x={0}
          y={0}
          dy={COMPARISON_TICK_DY}
          textAnchor="middle"
          fontSize={COMPARISON_TICK_FONT_SIZE}
          fill={color}
          opacity={COMPARISON_TICK_OPACITY}
          fontStyle="italic"
        >
          {comparison}
        </text>
      )}
    </g>
  )
}

/**
 * Pins x-axis ticks to the subset of timestamps that have an aligned comparison
 * date, so every rendered tick can show both dates (an unaligned tick would leave
 * a blank second line). Returns undefined when there's nothing to pin to, letting
 * recharts auto-pick. Short ranges (≤ the daily limit) keep every aligned day;
 * beyond that, an explicit `xAxisTickCount` picks evenly across the time domain
 * (snapping to aligned entries), otherwise it picks evenly by index.
 */
export function pickComparisonXTicks(
  data: ChartDataRow[],
  comparisonDateByTimestamp: Map<number, string>,
  xAxisTickCount?: number,
): number[] | undefined {
  if (comparisonDateByTimestamp.size === 0) return undefined
  if (data.length <= 1) return undefined
  const alignedTimestamps = data
    .map((d) => d.timestamp)
    .filter((ts) => comparisonDateByTimestamp.has(ts))
  if (alignedTimestamps.length <= 1) return undefined

  // Short ranges label every aligned day; thinning would drop a day and leave an
  // uneven gap (e.g. a 7-day view losing its middle date).
  if (xAxisTickCount == null && alignedTimestamps.length <= DAILY_TICK_LIMIT) {
    return alignedTimestamps
  }

  const desired = Math.min(
    xAxisTickCount ?? DEFAULT_X_TICK_COUNT,
    alignedTimestamps.length,
  )
  if (alignedTimestamps.length <= desired) return alignedTimestamps

  if (xAxisTickCount != null) {
    const lo = alignedTimestamps[0]
    const hi = alignedTimestamps[alignedTimestamps.length - 1]
    const range = hi - lo
    const picks: number[] = []
    for (let i = 0; i < desired; i++) {
      const target = lo + (range * i) / (desired - 1)
      const closest = alignedTimestamps.reduce((best, ts) =>
        Math.abs(ts - target) < Math.abs(best - target) ? ts : best,
      )
      if (!picks.includes(closest)) picks.push(closest)
    }
    return picks
  }

  const step = (alignedTimestamps.length - 1) / (desired - 1)
  const picks: number[] = []
  for (let i = 0; i < desired; i++) {
    picks.push(alignedTimestamps[Math.round(i * step)])
  }
  return picks
}
